<template>
  <table class="ocd">
    <tbody>
      <tr v-for="r in rows" :key="r.key" :class="rowClass(r)">
        <td v-if="r.gap" colspan="4" class="ocd-gap"></td>
        <template v-else>
          <calc-block-cell v-if="r.blockRows" :block="r.block" :rows="r.blockRows" compact />
          <td v-else-if="!r.block"></td>
          <td>
            {{ r.label }}<span v-if="r.extra" class="ocd-extra"> ({{ r.extra }})</span>
          </td>
          <td class="ocd-num" :class="r.sign">{{ r.value }}</td>
          <td class="ocd-num ocd-pct" :class="r.sign">{{ r.percent }}</td>
        </template>
      </tr>
    </tbody>
  </table>
</template>

<script>
import CalcBlockCell from "@/components/Calculation/CalcBlockCell.vue";

/**
 * Kompakte Kalkulation eines Angebots fuer Lightbox und Mouseover, gleiche Reihenfolge wie die Angebotskalkulation:
 * Bloecke A-D bis SKP, Gewinn/Verlust abgesetzt, dann VK netto/VAT/VK brutto, danach Rohertrag und DB1-DB3.
 */
export default {
  name: "OfferCalcDetail",
  components: { CalcBlockCell },
  props: {
    calc: { type: Object, required: true },
  },
  computed: {
    rows() {
      const c = this.calc;
      const t = (k) => this.$t(`offerCalculation.${k}`);
      const l = (k) => this.$t(`calcLabels.${k}`);
      const named = (short, name) => `${short} (${name})`;
      const line = (key, label, value, cls = "", extra = "") => ({ key, label, extra, value: this.eur(value), percent: "", cls, sign: "" });
      const result = (key, label, value, percent) => ({ key, label, value: this.eur(value), percent: percent == null ? "" : this.pct(percent), cls: "ocd-result", sign: this.sign(value) });
      const hasPurchase = c.purchasePriceEur != null;
      const rows = [];
      if (hasPurchase) {
        rows.push(
          {
            ...line("ekUsd", named(l("ek"), l("ekName")), null, "", c.exchangeRate != null ? this.$t("calcLabels.rate", { rate: this.num(c.exchangeRate, 3) }) : ""),
            value: c.purchasePriceUsd == null ? "–" : `${this.amount(c.purchasePriceUsd)} USD`,
          },
          line("ekEur", `= ${named(l("ek"), l("ekName"))}`, c.purchasePriceEur),
          line("bznk", `+ ${named(l("bznk"), l("bznkName"))}`, c.bznk),
          line("customs", `+ ${l("customs")}`, c.customs, "", c.customsRatePercent != null ? this.$t("calcLabels.customsRate", { rate: this.pct(c.customsRatePercent) }) : "")
        );
      }
      rows.push(
        line("bep", `${hasPurchase ? "= " : ""}${named("BEP", l("bepName"))}`, c.bep, "ocd-sum"),
        line("unloading", `+ ${t("containerUnloading")}`, c.containerUnloading),
        line("storage", `+ ${t("storage")}`, c.storage),
        line("additional", `+ ${t("additional")}`, c.additional),
        line("lap", `= ${named("LAP", l("lapName"))}`, c.lap, "ocd-sum"),
        line("pick", `+ ${t("pick")}`, c.pick),
        line("provisioning", `+ ${t("provisioning")}`, c.provisioning),
        line("shipping", `+ ${t("shipping")}`, c.shipping),
        line("bsp", `= ${named("BSP", l("bspName"))}`, c.bsp, "ocd-sum"),
        line("fixedCost", `+ ${t("fixedCost")}`, c.fixedCost),
        line("bkp", `= ${named("BKP", l("bkpName"))}`, c.bkp, "ocd-sum"),
        line("returnCost", `+ ${t("returnCost")}${(c.notes || []).includes("no_sales") ? ` (${this.$t("productCalculation.noSales90")})` : ""}`, c.returnCost),
        line("marketing", `+ ${t("marketing")}`, c.marketing),
        line("commission", `+ ${t("commission")}`, c.commission),
        line("skp", `= ${named("SKP", l("skpName"))}`, c.skp, "ocd-sum"),
        { key: "gapProfitTop", gap: true },
        { ...result("profit", t("profit"), c.profit, null), cls: `ocd-profit ${c.profit == null ? "" : c.profit >= 0 ? "ocd-profit--plus" : "ocd-profit--minus"}` },
        { key: "gapProfitBottom", gap: true },
        line("saleNet", named(l("vk"), l("vkName")), c.saleNet, "ocd-sum", l("net")),
        line("vat", l("vat"), c.vat, "", c.vatPercent != null ? this.pct(c.vatPercent, 0) : ""),
        line("saleGross", named(l("vk"), l("vkName")), c.saleGross, "ocd-sum ocd-gross", l("gross")),
        { key: "gapFigures", gap: true },
        result("grossProfit", t("grossProfit"), c.grossProfit, c.grossProfitPercent),
        result("db1", "DB1", c.db1, c.db1Percent),
        result("db2", "DB2", c.db2, c.db2Percent),
        result("db3", "DB3", c.db3, c.db3Percent)
      );
      // Kostenbloecke wie in der Kalkulation; der VK-Block gehoert wie dort wieder zu D
      const blocks = [
        ["a", [...(hasPurchase ? ["ekUsd", "ekEur", "bznk", "customs"] : []), "bep"]],
        ["b", ["unloading", "storage", "additional", "lap", "pick", "provisioning", "shipping", "bsp"]],
        ["c", ["fixedCost", "bkp"]],
        ["d", ["returnCost", "marketing", "commission", "skp"]],
        ["d", ["saleNet", "vat", "saleGross"]],
      ];
      blocks.forEach(([block, keys]) => {
        keys.forEach((key, i) => {
          const row = rows.find((r) => r.key === key);
          row.block = block;
          if (i === 0) row.blockRows = keys.length;
        });
      });
      return rows;
    },
  },
  methods: {
    rowClass(r) {
      return [r.cls, r.block ? `calc-row calc-row--${r.block}` : "", r.block && (r.cls || "").includes("ocd-sum") ? "calc-sum" : ""];
    },
    num(value, decimals) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    // Betraege immer mit zwei Nachkommastellen (formatNumberGerman kuerzt 57,90 auf 57,9)
    amount(value) {
      return this.$options.filters.formatUnit(value, "", 2, 2).trim();
    },
    eur(value) {
      return value == null ? "–" : `${this.amount(value)} €`;
    },
    pct(value, decimals = 1) {
      return value == null ? "" : `${this.num(value, decimals)} %`;
    },
    sign(value) {
      return value == null ? "" : value >= 0 ? "ocd-pos" : "ocd-neg";
    },
  },
};
</script>

<style scoped>
.ocd {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 300px;
  color: #444;
}
.ocd td {
  padding: 2px 6px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  white-space: nowrap;
}
.ocd .ocd-num {
  text-align: right;
}
.ocd .ocd-pct {
  min-width: 56px;
}
.ocd-extra {
  font-size: 11px;
  color: #888;
}
.ocd-sum td {
  font-weight: 600;
  border-top: 1px solid #ddd;
}
.ocd-gross td {
  font-weight: 700;
}
.ocd-result td {
  font-weight: 600;
}
.ocd .ocd-gap {
  height: 8px;
  padding: 0;
  border: none;
  background: #fff;
}
.ocd-profit td {
  font-weight: 700;
  font-size: 13px;
  border-bottom: none;
}
.ocd-profit--plus td {
  background: #f1f5e8;
  color: #7f915a;
}
.ocd-profit--minus td {
  background: #ffebee;
  color: #c62828;
}
.ocd-pos {
  color: #7f915a;
}
.ocd-neg {
  color: #c62828;
}
</style>
